import type { IconType } from 'react-icons';
import {
    FiActivity,
    FiCloud,
    FiCloudRain,
    FiDroplet,
    FiMap,
    FiRadio,
    FiThermometer,
    FiWind,
    FiZap,
} from 'react-icons/fi';
import { WiHurricane } from 'react-icons/wi';
import type { LayerKey } from '../store/types';
import { getIconByName } from './iconRegistry';
import { getLayerOverride } from './remoteConfig';

export type ForecastGridType =
    | 'wind'
    | 'wind-gusts'
    | 'precipitation'
    | 'radar-sim'
    | 'temperature'
    | 'feels-like'
    | 'wet-bulb'
    | 'k-index'
    | 'o-index'
    | 'pressure'
    | 'humidity'
    | 'dewpoint'
    | 'clouds'
    | 'sst-anomaly'
    | 'sst-anomaly-rel'
    | 't850-anomaly';

export type LayerKind = 'forecast-scalar' | 'forecast-vector' | 'tile-raster' | 'point-overlay';
export type TimeMode = 'forecast' | 'observed-past' | 'near-real-time' | 'static';
// 'mrms' حلّ محلّ 'rainviewer' في طبقة الرادار (رخصة تجارية سليمة + دقيقتان بدل
// عشر). أُبقي الاسم القديم خارج الاتحاد عمداً: بقاؤه كان سيسمح بالعودة إليه
// سهواً في طبقة جديدة، والقيد هنا رخصيّ لا تقنيّ.
export type DataSource = 'mrms' | 'nasa-gibs' | 'nasa-firms' | 'local';

export interface WeatherLayerConfig {
    id: LayerKey;
    labelAr: string;
    labelEn: string;
    kind: LayerKind;
    timeMode: TimeMode;
    source: DataSource;
    sourceLabel: string;
    variables: string[];
    unit: string;
    color: string;
    icon: IconType;
    attribution: string;
    apiType?: ForecastGridType;
    descriptionAr: string;
    opacity: number;
    updateIntervalMinutes: number;
}

export const FORECAST_LAYER_IDS: ForecastGridType[] = [
    'wind',
    'wind-gusts',
    'precipitation',
    'radar-sim',
    'temperature',
    'feels-like',
    'wet-bulb',
    'k-index',
    'o-index',
    'pressure',
    'humidity',
    'dewpoint',
    'clouds',
    't850-anomaly',
];

/**
 * كل الطبقات السلَّمية **القاعدية**: طبقات النموذج + طبقات الرصد المستقلّة عنه.
 *
 * الفرق عن FORECAST_LAYER_IDS جوهري ولا يجوز الخلط: تلك تعني «ما يولّده النموذج
 * المختار» وتُصفّى بـ meta.layers، وهذه تعني «ما يملأ الخريطة لوناً فيزاحم غيره».
 *
 * وُلد هذا الثابت من عطبٍ وقع فعلاً: طبقة شذوذ سطح البحر أُضيفت خارج
 * FORECAST_LAYER_IDS (وهو صحيح — ليست من النموذج)، فسقطت من قائمة «التبادل» في
 * setActiveLayer. النتيجة أن الضغط عليها كان **يشغّلها فوق طبقة أخرى ما زالت
 * تعمل**، فيلتقط منتقي الطبقة الفعّالة الطبقةَ الأقدم في الترتيب ولا تظهر الجديدة
 * أبداً — ضغطةٌ لا يتبعها شيء ولا خطأ في الكونسول يفسّرها.
 *
 * فمن يسأل «أي الطبقات تُطفئ الأخرى؟» يقرأ هذه، ومن يسأل «أيّها ولّدها النموذج؟»
 * يقرأ تلك.
 */
export const BASE_SCALAR_LAYER_IDS: LayerKey[] = [...FORECAST_LAYER_IDS, 'sst-anomaly', 'sst-anomaly-rel'];

/**
 * الطبقة الأساس الفعّالة (الرياح أو الطبقة السلَّمية) — مصدر واحد لكل من يحتاج «أي طبقة
 * تُعرض الآن؟»، كالتفضيلات المحفوظة لكل طبقة (خطوط الضغط مثلاً).
 */
export function getActiveBaseLayerKey(visibleLayers: Record<LayerKey, boolean>): LayerKey | null {
    if (visibleLayers.wind) return 'wind';
    return BASE_SCALAR_LAYER_IDS.find((l) => l !== 'wind' && visibleLayers[l]) ?? null;
}

/**
 * بلاطات **قاعدية** تتبادل مع الحقول السلَّمية ومع بعضها: القمر والرادار.
 *
 * الرادار طبقة قائمة بذاتها لا تراكب: يُفتح وحده، ويُطفئ ما تحته كما يفعل القمر
 * تماماً. وهو مرصود لا متوقَّع، فوضعه فوق حقل توقّع (حرارة أو حتى هطول) يضع كمّيتين
 * مختلفتَي الطبيعة على مساحة واحدة.
 *
 * وكونه قاعدةً يستلزم أن تكون له **حالة قاعدة خاصّة** في `styleWeatherBase` — بلاها
 * يسقط على الداكن العامّ ‏#0e1116 بمضلّعات شفّافة، وهو أصل «الخلفية السوداء» المرصودة.
 * (نفس ما حدث لطبقة شذوذ سطح البحر قبلها، وموثَّق هناك.)
 */
export const TILE_OVERLAY_LAYER_IDS: LayerKey[] = ['satellite', 'radar'];

/**
 * تراكبات رمزية تُرسم **فوق** أي خلفية ولا تنافسها.
 *
 * الأعاصير والحرائق رموز/مسارات لا حقول لونية تملأ الشاشة، فتشغيلها لا يستلزم إطفاء أي
 * طبقة قاعدة — بخلاف الحقول العددية والقمر والرادار التي يزاحم بعضها بعضاً على نفس
 * المساحة. الفصل هنا هو ما يسمح بظهورها فوق صور القمر الصناعي كما في Zoom Earth.
 */
export const OVERLAY_LAYER_IDS: LayerKey[] = ['hurricanes', 'wildfires'];

export const WEATHER_LAYER_CONFIGS: Record<LayerKey, WeatherLayerConfig> = {
    wind: {
        id: 'wind',
        labelAr: 'الرياح',
        labelEn: 'Wind speed',
        kind: 'forecast-vector',
        timeMode: 'forecast',
        source: 'local',
        sourceLabel: 'النموذج المختار: GFS أو ECMWF أو ICON (نُسج محلية)',
        variables: ['wind_speed_10m', 'wind_direction_10m'],
        unit: 'km/h',
        color: '#42a5f5',
        icon: FiWind,
        attribution: 'NOAA/NCEP GFS، ECMWF Open Data، DWD ICON — مُعالَجة محلياً',
        apiType: 'wind',
        descriptionAr: 'خريطة سرعة الرياح مع جسيمات متحركة فوقها لقراءة الاتجاه والحركة.',
        opacity: 0.66,
        updateIntervalMinutes: 60,
    },
    'wind-gusts': {
        id: 'wind-gusts',
        labelAr: 'هبّات الرياح',
        labelEn: 'Wind gusts',
        kind: 'forecast-scalar',
        timeMode: 'forecast',
        source: 'local',
        sourceLabel: 'NOAA GFS / ECMWF (نُسج محلية)',
        variables: ['wind_gusts_10m'],
        unit: 'km/h',
        color: '#64b5f6',
        icon: FiWind,
        attribution: 'NOAA/NCEP GFS, ECMWF Open Data — مُعالَجة محلياً',
        apiType: 'wind-gusts',
        descriptionAr: 'أعلى سرعة رياح متوقعة في الهبات، مفيدة لتمييز مناطق الخطر عن سرعة الرياح المتوسطة.',
        opacity: 0.64,
        updateIntervalMinutes: 60,
    },
    /*
     * رادار **متوقَّع** — حقل REFC من GFS: الانعكاسية التي «سيراها» الرادار كل ساعة قادمة.
     * هو ما يعرضه المقطع المرجعي (meteologix «Max reflectivity»)، ويسدّ فراغ الرصد حيث لا
     * رادار مفتوح (السعودية والخليج). ومنفصل عن طبقة `radar` عمداً: ذاك رصدٌ وقع، وهذا
     * توقّع قد يُخطئ — وخلطهما تحت اسم واحد يُفقد القارئ الفرق.
     */
    'radar-sim': {
        id: 'radar-sim',
        labelAr: 'رادار متوقَّع',
        labelEn: 'Forecast radar',
        kind: 'forecast-scalar',
        timeMode: 'forecast',
        source: 'local',
        sourceLabel: 'NOAA GFS — انعكاسية مركّبة (REFC)',
        variables: ['composite_reflectivity'],
        unit: 'dBZ',
        color: '#22c55e',
        icon: FiRadio,
        attribution: 'NOAA/NCEP GFS (ملك عام) — مُعالَجة محلياً',
        apiType: 'radar-sim',
        descriptionAr: 'انعكاسية الرادار كما يحاكيها نموذج GFS لكل ساعة قادمة في العالم كلّه — توقّع لا رصد.',
        opacity: 1,
        updateIntervalMinutes: 60,
    },
    precipitation: {
        id: 'precipitation',
        labelAr: 'هطول الأمطار',
        labelEn: 'Precipitation forecast',
        kind: 'forecast-scalar',
        timeMode: 'forecast',
        source: 'local',
        sourceLabel: 'النموذج المختار: GFS أو ECMWF أو ICON (مطر وثلج من نُسجه)',
        variables: ['precipitation', 'rain', 'snowfall'],
        unit: 'mm/h',
        color: '#1e88e5',
        icon: FiCloudRain,
        attribution: 'NOAA/NCEP GFS، ECMWF Open Data، DWD ICON — مُعالَجة محلياً',
        apiType: 'precipitation',
        descriptionAr: 'مصدر المطر والثلج هو النموذج المحدد في أعلى الخريطة؛ تُصنّف الجسيمات ثلجاً عندما تكون حرارة النموذج منخفضة.',
        opacity: 0.7,
        updateIntervalMinutes: 60,
    },
    temperature: {
        id: 'temperature',
        labelAr: 'درجة الحرارة',
        labelEn: 'Temperature',
        kind: 'forecast-scalar',
        timeMode: 'forecast',
        source: 'local',
        sourceLabel: 'NOAA GFS / ECMWF (نُسج محلية)',
        variables: ['temperature_2m'],
        unit: 'C',
        color: '#ef5350',
        icon: FiThermometer,
        attribution: 'NOAA/NCEP GFS, ECMWF Open Data — مُعالَجة محلياً',
        apiType: 'temperature',
        descriptionAr: 'درجة الحرارة عند ارتفاع مترين فوق السطح.',
        opacity: 0.66,
        updateIntervalMinutes: 60,
    },
    'feels-like': {
        id: 'feels-like',
        labelAr: 'الإحساس الحراري',
        labelEn: 'Feels like',
        kind: 'forecast-scalar',
        timeMode: 'forecast',
        source: 'local',
        sourceLabel: 'NOAA GFS / ECMWF (نُسج محلية)',
        variables: ['apparent_temperature'],
        unit: 'C',
        color: '#ff8a65',
        icon: FiThermometer,
        attribution: 'NOAA/NCEP GFS, ECMWF Open Data — مُعالَجة محلياً',
        apiType: 'feels-like',
        descriptionAr: 'درجة الحرارة المحسوسة بعد تأثير الرطوبة والرياح.',
        opacity: 0.64,
        updateIntervalMinutes: 60,
    },
    'wet-bulb': {
        id: 'wet-bulb',
        labelAr: 'اللمبة الرطبة',
        labelEn: 'Wet-bulb temperature',
        kind: 'forecast-scalar',
        timeMode: 'forecast',
        source: 'local',
        sourceLabel: 'مُشتقّة من الحرارة والرطوبة (GFS/ECMWF)',
        variables: ['temperature_2m', 'relative_humidity_2m'],
        unit: 'C',
        color: '#c81e96',
        icon: FiThermometer,
        attribution: 'مُشتقّة محلياً (صيغة Stull) من نُسج NOAA GFS / ECMWF',
        apiType: 'wet-bulb',
        descriptionAr: 'درجة حرارة اللمبة الرطبة — مؤشّر إجهاد حراري؛ تجاوز ~31°م خطير و~35°م حدّ بقاء الإنسان.',
        opacity: 0.66,
        updateIntervalMinutes: 60,
    },
    'k-index': {
        id: 'k-index',
        labelAr: 'K-Index',
        labelEn: 'K-index thunderstorm potential',
        kind: 'forecast-scalar',
        timeMode: 'forecast',
        source: 'local',
        sourceLabel: 'مُشتق من طبقات الحرارة والرطوبة العلوية',
        variables: ['o-index', 'precipitation'],
        unit: 'index',
        color: '#f59e0b',
        icon: FiZap,
        attribution: 'مُشتق محلياً من حقول النموذج العددي عند 850/700/500 hPa',
        apiType: 'k-index',
        descriptionAr: 'مؤشر احتمالية العواصف الرعدية من عدم الاستقرار والرطوبة في الطبقات المتوسطة؛ القيم الأعلى تعني بيئة أنسب للعواصف.',
        opacity: 0.7,
        updateIntervalMinutes: 60,
    },
    'o-index': {
        id: 'o-index',
        labelAr: 'عواصف رعدية',
        labelEn: 'Thunderstorms',
        kind: 'forecast-scalar',
        timeMode: 'forecast',
        source: 'local',
        sourceLabel: 'النموذج المختار: GFS أو ECMWF أو ICON — CAPE مع الهطول الحملي',
        variables: ['cape'],
        unit: 'l/km²',
        color: '#ec4899',
        icon: FiZap,
        attribution: 'تقدير محلي: CAPE × الهطول الحملي (GFS: CAPE+CPRAT، ICON: CAPE_ML+RAIN_CON). ليس قياساً مرصوداً — لا يوفّر أي من المصادر المجانية حقل صواعق.',
        apiType: 'o-index',
        descriptionAr: 'كثافة الصواعق المقدَّرة (ومضة/كم²/ساعة). تُحسب حيث تجتمع طاقة عدم الاستقرار مع هطول حملي نشط — فالطاقة وحدها لا تُنتج صواعق. تقدير مُعايَر على سلّم Windy، لا قياس مرصود.',
        opacity: 0.7,
        updateIntervalMinutes: 60,
    },
    pressure: {
        id: 'pressure',
        labelAr: 'الضغط الجوي',
        labelEn: 'Surface pressure',
        kind: 'forecast-scalar',
        timeMode: 'forecast',
        source: 'local',
        sourceLabel: 'NOAA GFS / ECMWF (نُسج محلية)',
        variables: ['surface_pressure'],
        unit: 'hPa',
        color: '#ab47bc',
        icon: FiActivity,
        attribution: 'NOAA/NCEP GFS, ECMWF Open Data — مُعالَجة محلياً',
        apiType: 'pressure',
        descriptionAr: 'ضغط السطح من النموذج، وتمهيد لاحق لخطوط الضغط.',
        // 0.95 لا 0.6. الشفافية المنخفضة كانت **نصف سبب البهتان**: حقل الضغط عند Zoom Earth
        // معتمٌ تماماً وتُرسم التضاريس فوقه، أمّا مزجُه بقاعدة فاتحة بنسبة 40% فيسحب كل
        // لون نحو الأبيض — وأشدّه أثراً حول التعادل حيث اللون شاحب أصلاً.
        opacity: 0.95,
        updateIntervalMinutes: 60,
    },
    humidity: {
        id: 'humidity',
        labelAr: 'الرطوبة النسبية',
        labelEn: 'Relative humidity',
        kind: 'forecast-scalar',
        timeMode: 'forecast',
        source: 'local',
        sourceLabel: 'NOAA GFS / ECMWF (نُسج محلية)',
        variables: ['relative_humidity_2m'],
        unit: '%',
        color: '#26c6da',
        icon: FiDroplet,
        attribution: 'NOAA/NCEP GFS, ECMWF Open Data — مُعالَجة محلياً',
        apiType: 'humidity',
        descriptionAr: 'الرطوبة النسبية عند ارتفاع مترين.',
        opacity: 0.62,
        updateIntervalMinutes: 60,
    },
    dewpoint: {
        id: 'dewpoint',
        labelAr: 'نقطة الندى',
        labelEn: 'Dew point',
        kind: 'forecast-scalar',
        timeMode: 'forecast',
        source: 'local',
        sourceLabel: 'NOAA GFS / ECMWF (نُسج محلية)',
        variables: ['dew_point_2m'],
        unit: 'C',
        color: '#00acc1',
        icon: FiDroplet,
        attribution: 'NOAA/NCEP GFS, ECMWF Open Data — مُعالَجة محلياً',
        apiType: 'dewpoint',
        descriptionAr: 'طبقة مستقلة لفهم الرطوبة الفعلية وليس النسبة فقط.',
        opacity: 0.62,
        updateIntervalMinutes: 60,
    },
    clouds: {
        id: 'clouds',
        labelAr: 'الغيوم',
        labelEn: 'Cloud cover',
        kind: 'forecast-scalar',
        timeMode: 'forecast',
        source: 'local',
        sourceLabel: 'NOAA GFS / ECMWF (نُسج محلية)',
        variables: ['cloud_cover'],
        unit: '%',
        color: '#90a4ae',
        icon: FiCloud,
        attribution: 'NOAA/NCEP GFS, ECMWF Open Data — مُعالَجة محلياً',
        apiType: 'clouds',
        descriptionAr: 'تغطية سحابية متوقعة من النموذج.',
        // **1 لا 0.5.** شفافية الغيوم محسوبة في نقاط السلّم نفسها (‏التغطية⁴ — راجع
        // CLOUD_HEATMAP_STOPS)، وعتامةُ الطبقة تُضرب فيها. فـ0.5 كانت تهبط بالغطاء
        // الكامل من 1 إلى 0.45، أي تكتم أوضح ما في الحقل بينما يبقى الضباب الخفيف.
        opacity: 1,
        updateIntervalMinutes: 60,
    },
    /**
     * شذوذ حرارة 850 هكتوباسكال — حقل توقّع عادي من GFS، لكنه **فرقٌ لا قياس**.
     *
     * ثلاثة فروق تُميّزه عن بقيّة طبقات النموذج ويجب ألّا تُنسى عند تعديله:
     *
     *  ١. **الصفر وسط السلّم لا طرفه.** الاتجاهان (أدفأ/أبرد) متكافئان، فأي إخلال
     *     بالتماثل حول الصفر يجعل الخريطة توحي بميل ليس في البيانات.
     *  ٢. **النماذج الثلاثة تطرح المناخ نفسه.** المرجع واحد مشترك (‏g.t850_anomaly)
     *     لا نسخة في كل سكربت. وهذا شرط صحّة لا ترتيب: لو حسب كلٌّ مرجعه لصار
     *     اختلاف الخرائط عند تبديل النموذج اختلافَ مراجع لا اختلافَ توقّعات — وهو
     *     يبطل المقارنة التي يبدّل المستخدم النموذج لأجلها أصلاً.
     *  ٣. **حزمه منفصلة لا متدرّجة** (راجع T850_ANOMALY_STOPS) — وهذا مقصود لأن
     *     قراءته كمّية بالعتبات.
     */
    't850-anomaly': {
        id: 't850-anomaly',
        labelAr: 'شذوذ حرارة 850 hPa',
        labelEn: '850 hPa temp anomaly',
        kind: 'forecast-scalar',
        timeMode: 'forecast',
        source: 'local',
        sourceLabel: 'النموذج المختار (‏GFS أو ECMWF أو ICON) ناقص مناخ NCEP‏ 1981–2010 (نُسج محلية)',
        variables: ['t850_anomaly'],
        unit: '°م',
        color: '#d94f45',
        icon: FiThermometer,
        attribution: 'NOAA GFS، ECMWF Open Data، DWD ICON + مناخ NCEP/NCAR — مُعالَجة محلياً',
        apiType: 't850-anomaly',
        descriptionAr: 'فرق حرارة الجو عند 850 هكتوباسكال (نحو 1.5 كم) عن معدّله المناخي 1981–2010. يكشف الكتلة الهوائية نفسها بعيداً عن ضجيج السطح: الأحمر موجة حرّ قادمة والبنفسجي اقتحام بارد.',
        // 0.88 لا 0.7 كبقيّة حقول النموذج، لنفس سبب طبقة المحيطات: سلّمٌ صفرُه **أبيض**
        // يعني «لا شذوذ». والشفافية تمزج ذلك الأبيض بالقاعدة الداكنة فيغدو رمادياً،
        // والرمادي على خريطة يُقرأ «لا بيانات» لا «لا شذوذ» — فتنقلب أوضح مناطق الخريطة
        // إلى أغمضها. وهي أيضاً ما يحفظ تطابق الألوان مع المرجع الذي نُقل عنه السلّم.
        opacity: 0.88,
        updateIntervalMinutes: 60,
    },
    /**
     * شذوذ حرارة سطح البحر — طبقة النينيو/النينيا.
     *
     * **الطبقة الوحيدة هنا التي محورها الزمني أيّام رصد ماضية لا ساعات توقّع.**
     * لذلك رواسترها في `rasters/ocean/` بمانيفست خاصّ لا في `rasters/meta.json`
     * (راجع الترويسة في oisst_to_raster.py). أي محاولة لتمريرها عبر مسار الرواستر
     * العادي ستقرأ «يوماً» في خانة «ساعة» وتضع الإطارات في زمن خاطئ تماماً.
     *
     * وهي أيضاً حقل **بحري بحت**: قيم اليابسة حشوٌ ممدود من الساحل ليصحّ الاستيفاء
     * عند الحدّ، ويُخفيها قناع البرّ في الشيدر — لا تُقرأ ولا تُعرض.
     */
    'sst-anomaly': {
        id: 'sst-anomaly',
        labelAr: 'حرارة المحيطات — النينيو',
        labelEn: 'Sea surface temp anomaly',
        kind: 'forecast-scalar',
        timeMode: 'observed-past',
        source: 'local',
        sourceLabel: 'NOAA OISST v2.1 — يومي، 0.25° (مُعالَج محلياً)',
        variables: ['sst_anomaly'],
        unit: '°م',
        color: '#d6604d',
        icon: FiThermometer,
        attribution: 'NOAA NCEI OISST v2.1 — Public Domain',
        apiType: 'sst-anomaly',
        descriptionAr: 'فرق حرارة سطح البحر عن معدّله المناخي (1971–2000). الأحمر أدفأ من المعتاد والأزرق أبرد — وهو ما تُقاس به ظاهرتا النينيو والنينيا.',
        opacity: 0.9,
        updateIntervalMinutes: 1440,
    },
    /**
     * النسخة **النسبية** من طبقة المحيط: نفس بيانات OISST بعد طرح متوسّط شذوذ
     * النطاق المداري (‏20°ج–20°ش) من كل خليّة.
     *
     * ليست تجميلاً بل إجابة عن سؤال مختلف. المطلق يجيب «كم يبلغ الشذوذ؟»، والنسبي
     * يجيب «أين يقع الحدث؟». ومناخ OISST المرجعي (1971–2000) قديم فيه احترار
     * مخبوز — متوسّط المداري اليوم ‎+0.90 °م — فيظهر المحيط كلّه أحمر ويغرق لسان
     * النينيو داخل الخلفية. الطرح يزيل تلك الخلفية فتبقى الإشارة المكانية وحدها.
     */
    'sst-anomaly-rel': {
        id: 'sst-anomaly-rel',
        labelAr: 'حرارة المحيطات — نسبي',
        labelEn: 'SST anomaly (relative)',
        kind: 'forecast-scalar',
        timeMode: 'observed-past',
        source: 'local',
        sourceLabel: 'NOAA OISST v2.1 — بعد طرح متوسّط المداري (مُعالَج محلياً)',
        variables: ['sst_anomaly_relative'],
        unit: '°م',
        color: '#d6604d',
        icon: FiThermometer,
        attribution: 'NOAA NCEI OISST v2.1 — Public Domain',
        apiType: 'sst-anomaly-rel',
        descriptionAr: 'شذوذ حرارة سطح البحر بعد طرح متوسّط المحيط المداري — يعزل النينيو/النينيا عن الاحترار العام فيبرز الحدث بوضوح.',
        opacity: 0.9,
        updateIntervalMinutes: 1440,
    },
    radar: {
        id: 'radar',
        labelAr: 'رادار المطر',
        labelEn: 'Observed radar',
        kind: 'tile-raster',
        timeMode: 'observed-past',
        source: 'mrms',
        sourceLabel: 'NOAA MRMS',
        variables: ['radar-reflectivity'],
        unit: 'dBZ',
        color: '#64b5f6',
        icon: FiRadio,
        // الإسناد ليس تجميلاً بل بيانٌ معروض للمستخدم عن مصدر ما يراه. تركُه على
        // «RainViewer» بعد التبديل كان سيُنسب البيانات إلى جهة لم تعد تُنتجها —
        // وهو خطأ في المعلومة قبل أن يكون خطأً في الرخصة.
        attribution: 'NOAA MRMS (public domain)',
        descriptionAr: 'أمطار مرصودة شبه حية للولايات المتحدة، وليست توقعات النموذج.',
        opacity: 0.72,
        // MRMS ينشر كل دقيقتين — والرقم يُقرأ في واجهة حالة الطبقات، فبقاؤه 10
        // كان يُخبر المستخدم أن الرادار أقدم بخمسة أضعاف مما هو عليه فعلاً.
        updateIntervalMinutes: 2,
    },
    satellite: {
        id: 'satellite',
        labelAr: 'قمر صناعي',
        labelEn: 'Satellite imagery',
        kind: 'tile-raster',
        timeMode: 'near-real-time',
        source: 'nasa-gibs',
        sourceLabel: 'NASA GIBS',
        variables: ['VIIRS_SNPP_CorrectedReflectance_TrueColor'],
        unit: 'imagery',
        color: '#8bc34a',
        icon: FiMap,
        attribution: 'NASA GIBS',
        descriptionAr: 'صور أقمار صناعية مجانية عبر WMTS، وقد تتأخر عن الزمن الحقيقي.',
        opacity: 0.82,
        updateIntervalMinutes: 180,
    },
    hurricanes: {
        id: 'hurricanes',
        labelAr: 'الأعاصير',
        labelEn: 'Tropical cyclones',
        kind: 'point-overlay',
        timeMode: 'near-real-time',
        source: 'local',
        sourceLabel: 'Local tracker',
        variables: ['track', 'category', 'wind'],
        unit: 'track',
        color: '#ff5722',
        // دوّامة إعصار لا صاعقة: البرق طبقة أخرى تماماً، والصاعقة هنا كانت توحي بها.
        icon: WiHurricane,
        attribution: 'Local demo tracker',
        descriptionAr: 'متتبع أنظمة مدارية، ويحتاج مصدرًا رسميًا لاحقًا للإنتاج.',
        opacity: 1,
        updateIntervalMinutes: 60,
    },
    wildfires: {
        id: 'wildfires',
        labelAr: 'الحرائق',
        labelEn: 'Wildfires',
        kind: 'point-overlay',
        timeMode: 'near-real-time',
        source: 'nasa-firms',
        sourceLabel: 'NASA FIRMS',
        variables: ['hotspots'],
        unit: 'points',
        color: '#ff7043',
        icon: FiZap,
        attribution: 'NASA FIRMS, free key required for production',
        descriptionAr: 'نقاط ساخنة من FIRMS، تحتاج مفتاحًا مجانيًا للإنتاج.',
        opacity: 1,
        updateIntervalMinutes: 180,
    },
};

export function getLayerConfig(layer: LayerKey): WeatherLayerConfig {
    return getEffectiveLayerConfig(layer);
}

/**
 * الإعداد الفعلي للطبقة: الافتراضي المضمّن مدموجاً مع تخصيصات الأدمن
 * (من /api/v1/config بعد الترطيب). بدون باك إند يعود الافتراضي كما هو.
 */
export function getEffectiveLayerConfig(layer: LayerKey): WeatherLayerConfig {
    const base = WEATHER_LAYER_CONFIGS[layer];
    const override = getLayerOverride(layer);
    if (!override) return base;
    return {
        ...base,
        labelAr: override.label_ar || base.labelAr,
        labelEn: override.label_en || base.labelEn,
        color: override.color || base.color,
        opacity: override.opacity ?? base.opacity,
        icon: getIconByName(override.icon) ?? base.icon,
    };
}
